import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, PieChart, Pie, Cell, LabelList } from "recharts";
import { ArrowDownRight, ArrowUpRight } from "lucide-react";

export const COLORS = ["#4F46E5", "#0D9488", "#D97706", "#0284C7", "#7C3AED", "#E11D48", "#059669", "#475569"];
const GRID = { stroke: "#E2E8F0", strokeDasharray: "3 3", vertical: false };
const TICK = { fill: "#64748B", fontSize: 12 };
const TIP = { contentStyle: { background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, boxShadow: "0 10px 15px -3px rgba(0,0,0,.08)", padding: "10px 14px", fontSize: 13 } };

export function ChartCard({ title, sub, testid, className = "", children }) {
  return (
    <div className={`tp-card p-5 ${className}`} data-testid={testid}>
      <div className="mb-3">
        <div className="font-semibold text-slate-900 text-sm">{title}</div>
        {sub && <div className="text-xs text-slate-500 mt-0.5">{sub}</div>}
      </div>
      {children}
    </div>
  );
}

export const Empty = ({ h = 220 }) => <div className="grid place-items-center text-sm text-slate-400" style={{ height: h }}>No data in this period</div>;

export function KpiCard({ label, value, delta, testid, suffix = "" }) {
  const up = delta >= 0;
  return (
    <div className="tp-card p-4 flex flex-col justify-between hover:shadow-md transition-shadow" data-testid={testid}>
      <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1">{label}</div>
      <div className="text-2xl font-extrabold font-display text-slate-900 tracking-tight">{value ?? "—"}{value != null && suffix}</div>
      {delta == null ? <div className="text-[11px] text-slate-400 mt-2">No prior data</div> : (
        <span className={`self-start inline-flex items-center gap-0.5 text-[11px] font-medium px-2 py-0.5 rounded-full border mt-2 ${up ? "text-emerald-700 bg-emerald-50 border-emerald-200/60" : "text-rose-700 bg-rose-50 border-rose-200/60"}`}>
          {up ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}{Math.abs(delta)}%
        </span>
      )}
    </div>
  );
}

export function Bars({ data, x, bars, h = 260, horizontal = false, labels = false, onPick }) {
  if (!data?.length || !data.some((d) => bars.some((b) => d[b.key]))) return <Empty h={h} />;
  return (
    <ResponsiveContainer width="100%" height={h}>
      <BarChart data={data} layout={horizontal ? "vertical" : "horizontal"} margin={{ top: 8, right: labels ? 36 : 8, left: horizontal ? 8 : -12, bottom: 0 }} barGap={3}>
        <CartesianGrid {...GRID} vertical={horizontal} horizontal={!horizontal} />
        {horizontal ? <><XAxis type="number" hide /><YAxis type="category" dataKey={x} tick={{ ...TICK, fill: "#0F172A" }} width={130} axisLine={false} tickLine={false} /></>
          : <><XAxis dataKey={x} tick={TICK} axisLine={{ stroke: "#CBD5E1" }} tickLine={false} interval="preserveStartEnd" /><YAxis tick={TICK} axisLine={false} tickLine={false} allowDecimals={false} /></>}
        <Tooltip {...TIP} cursor={{ fill: "rgba(79,70,229,0.05)" }} />
        {bars.length > 1 && <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12, paddingTop: 6 }} itemSorter={(it) => bars.findIndex((b) => b.key === it.dataKey)} />}
        {bars.map((b, i) => (
          <Bar key={b.key} dataKey={b.key} name={b.name} fill={b.color || COLORS[i]} cursor={onPick ? "pointer" : undefined} onClick={onPick ? (d) => onPick(d?.payload ?? d, b.key) : undefined} radius={horizontal ? [0, 6, 6, 0] : [6, 6, 0, 0]} maxBarSize={horizontal ? 30 : 38}>
            {b.cells && data.map((_, k) => <Cell key={k} fill={b.cells[k % b.cells.length]} />)}
            {labels && <LabelList dataKey={b.key} position={horizontal ? "right" : "top"} style={{ fontSize: 12, fontWeight: 600, fill: "#334155" }} />}
          </Bar>
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}

export function Donut({ data, h = 230, onPick, testid }) {
  const rows = (data || []).filter((d) => d.value).slice(0, 8);
  if (!rows.length) return <Empty h={h} />;
  const total = rows.reduce((s, r) => s + r.value, 0);
  return (
    <div className="flex flex-col sm:flex-row items-center gap-4">
      <div className="relative w-full sm:w-1/2" style={{ height: h }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={rows} dataKey="value" nameKey="name" innerRadius="60%" outerRadius="85%" paddingAngle={rows.length > 1 ? 3 : 0} stroke="none" cursor={onPick ? "pointer" : undefined} onClick={onPick ? (d) => onPick(d?.payload?.name ?? d?.name) : undefined}>
              {rows.map((_, i) => <Cell key={i} fill={COLORS[i % 8]} />)}
            </Pie>
            <Tooltip {...TIP} />
          </PieChart>
        </ResponsiveContainer>
        <div className="absolute inset-0 grid place-items-center pointer-events-none">
          <div className="text-center"><div className="text-2xl font-bold font-display text-slate-900">{total}</div><div className="text-[11px] text-slate-500 uppercase tracking-wider">Total</div></div>
        </div>
      </div>
      <ul className="w-full sm:w-1/2 space-y-1.5">
        {rows.map((r, i) => (
          <li key={r.name} className={`flex items-center gap-2 text-xs rounded px-1 -mx-1 ${onPick ? "cursor-pointer hover:bg-slate-50" : ""}`} onClick={onPick ? () => onPick(r.name) : undefined}
            data-testid={testid ? `${testid}-item-${i}` : undefined}>
            <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: COLORS[i % 8] }} />
            <span className="text-slate-700 truncate flex-1" title={r.name}>{r.name}</span>
            <span className="font-semibold text-slate-900">{r.value}</span>
            <span className="text-slate-400 w-10 text-right">{Math.round((r.value / total) * 100)}%</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
